import React, { Suspense } from "react";
import ScrollToTop from "components/ScrollToTop";
import { PageCacheProvider } from "contexts/pageCacheContext";
import { getUrlSuffix } from "utils/localStorageService";
import history from "utils/history";
import { configureLoggedSetter } from "utils/api";
import {
  configureLoggedSetter as configureAdminLoggedSetter
} from "utils/adminApi";
import { Redirect, Route, Router, Switch, useLocation } from "react-router-dom";
import { positions, Provider as AlertProvider, transitions } from "react-alert";
import AlertTemplate from "components/AlertTemplate";
import * as Sentry from "@sentry/react";
import DBConfig from "utils/DBConfig";
import { initDB } from "react-indexed-db";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Helmet } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { FlagsProvider, useFlags } from "contexts/flagsContext";
import { AuthProvider, useAuth } from "contexts/authContext";
import ErrorPage from "pages/Error";

// Lazy load all major page components
const Terms = React.lazy(() => import("pages/info/Terms"));
const Privacy = React.lazy(() => import("pages/info/Privacy"));
const ClientPipeline = React.lazy(() => import("pages/client/Page"));
const ClientLogin = React.lazy(() => import("pages/client/Login"));
const OnboardEntry = React.lazy(() => import("pages/talent/OnboardEntry"));
const AdminPage = React.lazy(() => import("pages/admin/Page"));
const TalentPage = React.lazy(() => import("pages/talent/Page"));
const TalentReferralProgram = React.lazy(() =>
  import("pages/talent/ReferralProgram")
);
const TalentLogin = React.lazy(() => import("pages/talent/Login"));
const StaffLogin = React.lazy(() => import("pages/admin/Login"));
const McpConnect = React.lazy(() => import("pages/admin/McpConnect"));
const TalentReview = React.lazy(() => import("pages/talent/TalentReview"));
const EngagementsPage = React.lazy(() => import("pages/engagements/Page"));
const TalentRegister = React.lazy(() => import("pages/talent/Register"));
const TalentRegisterPassword = React.lazy(() => import("pages/talent/RegisterPassword"));
const TalentResetPassword = React.lazy(() => import("./pages/talent/ResetPassword"));
const TalentResetPasswordSuccess = React.lazy(() => import("pages/talent/ResetPasswordSuccess"));
const TalentInviteLinkSent = React.lazy(() => import("pages/talent/InviteLinkSent"));
const TalentResetPasswordConfirm = React.lazy(() => import("pages/talent/ResetPasswordConfirm"));
const NotFoundPage = React.lazy(() => import("./pages/talent/NotFound"));
const NotAllowedPage = React.lazy(() => import("./pages/client/NotAllowed"));
const ChooseExperience = React.lazy(() => import("pages/client/ChooseExperience"));

// Loading component for Suspense fallback
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="text-midnight">Loading...</div>
  </div>
);

initDB(DBConfig);
const queryClient = new QueryClient();

Sentry.init({
  dsn: import.meta.env.REACT_APP_SENTRY_DSN,
  autoSessionTracking: true,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 1.0,
  environment: import.meta.env.REACT_APP_SENTRY_ENV,
  ignoreErrors: [
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications"
  ]
});

const alertOptions = {
  position: positions.TOP_CENTER,
  timeout: 0,
  offset: ".5rem",
  transition: transitions.FADE,
  // Above react-select `menuPortal` (z-index 9999) so toasts are visible while dropdowns stay open
  containerStyle: { marginTop: "4rem", zIndex: 10050 },
};

const AppErrorBoundary = ({ children }) => {
  const location = useLocation();
  return (
    <Sentry.ErrorBoundary
      fallback={<ErrorPage />}
      resetKeys={[location.pathname, location.search]}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

const AppContent = () => {
  const { flags } = useFlags();
  const auth = useAuth();
  const suffix = getUrlSuffix();
  const canRenderRoutes = Boolean(flags) || !auth.isLoggedIn;

  configureLoggedSetter(auth.handleUnauthorized);
  configureAdminLoggedSetter(auth.handleUnauthorized);

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, noarchive, nofollow" />
      </Helmet>
      <GoogleOAuthProvider clientId={import.meta.env.REACT_APP_GOOGLE_CLIENT_ID}>
        <QueryClientProvider client={queryClient}>
          <AlertProvider template={AlertTemplate} {...alertOptions}>
            <Router history={history}>
              <AppErrorBoundary>
              <ScrollToTop />
              {!canRenderRoutes ? (
                <LoadingFallback />
              ) : (
                <Suspense fallback={<LoadingFallback />}>
                  <Switch>
                    <Route exact path="/404">
                      <NotFoundPage />
                    </Route>
                    <Route exact path="/403">
                      <NotAllowedPage />
                    </Route>
                    <Route exact path="/terms">
                      <Terms />
                    </Route>
                    <Route exact path="/privacy">
                      <Privacy />
                    </Route>
                    <Route
                      exact
                      path="/mcp/connect"
                      render={({ location }) =>
                        auth.isAdmin ? (
                          <McpConnect />
                        ) : (
                          <Redirect
                            to={{
                              pathname: "/admin/login",
                              state: {
                                from: `${location.pathname}${location.search}`,
                              },
                            }}
                          />
                        )
                      }
                    />
                    <Route exact path="/login">
                      {auth.isAdmin ? (
                        <Redirect to="/admin" />
                      ) : auth.isTalent && auth.isClient ? (
                        <Redirect to="/select-experience" />
                      ) : auth.isTalent && suffix ? (
                        <Switch>
                          <Redirect to={`/talent/${suffix}`} />
                        </Switch>
                      ) : auth.isClient ? (
                        <Redirect to="/client" />
                      ) : (
                        <TalentLogin />
                      )}
                    </Route>
                    <Route exact path="/client/login">
                      <ClientLogin />
                    </Route>
                    <Route exact path="/select-experience">
                      {auth.isAdmin ? (
                        <Redirect to="/admin" />
                      ) : auth.isTalent && auth.isClient ? (
                        <ChooseExperience />
                      ) : auth.isTalent ? (
                        <Redirect to="/talent" />
                      ) : auth.isClient ? (
                        <Redirect to="/client" />
                      ) : (
                        <Redirect to="/login" />
                      )}
                    </Route>
                    <Route path="/client">
                      {!auth.isClient ? (
                        <Redirect to="/client/login" />
                      ) : (
                        <ClientPipeline />
                      )}
                    </Route>
                    <Route exact path="/talent/onboard">
                      <OnboardEntry
                        flags={flags}
                        isTalentSession={auth.isTalent}
                      />
                    </Route>
                    <Route path="/talent">
                      <Route exact path="/talent/login">
                        <Redirect to="/login" />
                      </Route>
                      {auth.isTalent ? (
                        <Switch>
                          <Route exact path="/talent/register-password">
                            <Redirect to="/talent" />
                          </Route>
                          <Route exact path="/talent/register">
                            <Redirect to="/talent" />
                          </Route>
                          <Route exact path="/talent/reset-password">
                            <Redirect to="/talent" />
                          </Route>
                          <Route exact path="/talent/reset-password-success">
                            <Redirect to="/talent" />
                          </Route>
                          <Route path="/talent/reset-password-confirm">
                            <Redirect to="/talent" />
                          </Route>
                          <Route path="/talent/null">
                            <Redirect to="/404" />
                          </Route>
                          <Route exact path="/talent/referral-program">
                            <TalentReferralProgram />
                          </Route>
                          <Route
                            path="/talent/:id"
                            render={({ location }) => (
                              auth.isLoggedIn ? (
                                <TalentPage />
                              ) : (
                                <Redirect
                                  to={{
                                    pathname: "/login",
                                    state: {
                                      from: `${location.pathname}${location.search}${location.hash || ""}`
                                    }
                                  }}
                                />
                              )
                            )}
                          />
                          <Route path="/talent">
                            <Redirect to={`/talent/${suffix}`} />
                          </Route>
                        </Switch>
                      ) : (
                        <Switch>
                          <Route exact path="/talent/register-password">
                            <TalentRegisterPassword />
                          </Route>
                          <Route exact path="/talent/register">
                            <TalentRegister />
                          </Route>
                          <Route exact path="/talent/reset-password">
                            <TalentResetPassword />
                          </Route>
                          <Route exact path="/talent/reset-password-success">
                            <TalentResetPasswordSuccess />
                          </Route>
                          <Route exact path="/talent/invite-link-sent">
                            <TalentInviteLinkSent />
                          </Route>
                          <Route path="/talent/reset-password-confirm">
                            <TalentResetPasswordConfirm />
                          </Route>
                          <Route
                            exact
                            path="/talent/referral-program"
                            render={({ location }) => (
                              <Redirect
                                to={{
                                  pathname: "/login",
                                  search: `?next=${encodeURIComponent(
                                    `${location.pathname}${location.search}${location.hash || ""}`
                                  )}`,
                                  state: {
                                    from: `${location.pathname}${location.search}${location.hash || ""}`
                                  }
                                }}
                              />
                            )}
                          />
                          <Route
                            path="/talent/:id"
                            render={({ location }) => (
                              auth.isLoggedIn ? (
                                <TalentPage />
                              ) : (
                                <Redirect
                                  to={{
                                    pathname: "/login",
                                    search: `?next=${encodeURIComponent(
                                      `${location.pathname}${location.search}${location.hash || ""}`
                                    )}`,
                                    state: {
                                      from: `${location.pathname}${location.search}${location.hash || ""}`
                                    }
                                  }}
                                />
                              )
                            )}
                          />
                          <Route path="/talent">
                            <Redirect to="/login" />
                          </Route>
                        </Switch>
                      )}
                    </Route>
                    <Route path="/admin">
                      <Switch>
                        <Route exact path="/admin/login">
                          <StaffLogin />
                        </Route>
                        {auth.isAdmin ? (
                          <Route path="/admin">
                            <PageCacheProvider>
                              {(loaded) => (
                                <>
                                  {loaded && (
                                    <AdminPage
                                      {...{}}
                                    />
                                  )}
                                </>
                              )}
                            </PageCacheProvider>
                          </Route>
                        ) : (
                          <Route path="/admin">
                            <Redirect to="/admin/login" />
                          </Route>
                        )}
                      </Switch>
                    </Route>
                    <Route path="/engagements/review/:id">
                      <TalentReview />
                    </Route>
                    <Route exact path="/engagements">
                      <Redirect to="/404" />
                    </Route>
                    {
                      flags?.talent_post_engagement_survey_enabled &&
                        <Route path="/engagements/candidate/:project_candidate_id">
                          <EngagementsPage />
                        </Route>
                    }
                    <Route path="/">
                      {auth.isAdmin ? (
                        <Redirect to="/admin" />
                      ) : auth.isTalent ? (
                        suffix === "null" || !suffix ? (
                          <Redirect to="/talent" />
                        ) : (
                          <Switch>
                            <Redirect to={`/talent/${suffix}`} />
                          </Switch>
                        )
                      ) : auth.isClient ? (
                        <Redirect to="/client" />
                      ) : (
                        <Redirect to="/login" />
                      )}
                    </Route>
                  </Switch>
                </Suspense>
              )}
              </AppErrorBoundary>
            </Router>
          </AlertProvider>
        </QueryClientProvider>
      </GoogleOAuthProvider>
    </>
  );
};

const App = () => (
  <AuthProvider>
    <FlagsProvider>
      <AppContent />
    </FlagsProvider>
  </AuthProvider>
);

export default App;